import type {
  ExclusionReason,
  FieldDescriptor,
  FieldKind,
  FieldOption,
  FieldScalar,
  FieldValue,
  FieldValueElement,
  FormUpdate,
  PageForm,
  PageForms,
} from "../../../services/ChatService";
import type { KitFieldKind } from "../../form/kitFieldRegistry";

export type { ExclusionReason, FieldDescriptor, FieldKind, FieldOption, FieldScalar, FieldValue, FieldValueElement, FormUpdate, PageForm, PageForms };

/** A wire field kind or "unsupported" — what discovery knows about a field before exclusions are applied. */
export type DiscoveredKind = KitFieldKind;

/** A value the bridge has read from or written to a field; `undefined` when the field holds nothing the wire can carry. */
export type BridgeValue = FieldValue | undefined;

/**
 * Kinds the bridge never sets: an input whose value the wire has no shape for. Such a field is
 * also excluded at discovery, so this is the apply path's own guard against a stale snapshot.
 */
export const UNSETTABLE_KINDS: ReadonlySet<DiscoveredKind> = new Set<DiscoveredKind>(["unsupported"]);

export function isSettableKind(kind: DiscoveredKind): boolean {
  return !UNSETTABLE_KINDS.has(kind);
}

/**
 * Why a value was refused beyond the rejection reason — a closed set so the chat can translate
 * it. "validator" is the kit input's own validator saying no; its name travels separately.
 */
export type RejectionDetail =
  | "not-integer"
  | "not-number"
  | "not-boolean"
  | "no-options"
  | "not-an-option"
  | "not-an-array"
  | "not-a-string"
  | "not-a-date-time"
  | "not-a-single-value"
  | "unsettable-kind"
  | "validator";

/** Outcome of asking an adapter to set a value. */
export type ApplyOutcome = { applied: true } | { applied: false; reason: "invalid" | "unsupported"; detail?: RejectionDetail; validator?: string };

/**
 * Uniform access to one rendered field, whatever renders it. The DOM adapter is the baseline;
 * a kit adapter wraps the registry handle of a form-kit input and reads model-space values and
 * validators from it instead. Both describe a field the same way and apply values through the
 * field's own change path, so callers never branch on the adapter type.
 */
export interface FieldAdapter {
  /** The element that carries the field (the wrapper div for a searchable select); undefined for a registry handle without one. */
  readonly element: HTMLElement | undefined;
  readonly kind: DiscoveredKind;
  /** Full option list for select-like kinds; the snapshot caps it, coercion checks membership against all of it. */
  options(): FieldOption[] | undefined;
  currentValue(): BridgeValue;
  /** The intrinsic part of the descriptor: path, kind, value, options, required, readOnly, error. */
  describe(): FieldDescriptor;
  /** Validates first, then sets the value the way a user change would. */
  apply(value: FieldValue): ApplyOutcome;
  /** Puts a value the field held before back without validating it — it was the user's own state. */
  restore(value: BridgeValue): void;
}

export interface DiscoveredField {
  path: string;
  adapter: FieldAdapter;
  /** The complete descriptor as the snapshot sends it — labels, hints and exclusions applied. */
  descriptor: FieldDescriptor;
}

export interface DiscoveredForm {
  formId: string;
  title: string;
  element: HTMLFormElement;
  /** True when the form is a form-kit `<Form>` (its id is the kit's `data-form-id`). */
  kit: boolean;
  fields: DiscoveredField[];
}

/** One value the bridge set, with what the field held before, so it can be put back. */
export interface AppliedChange {
  formId: string;
  path: string;
  previousValue: BridgeValue;
  appliedValue: FieldValue;
  /** The field's label as the snapshot describes it. */
  label: string;
  /** The applied value as the admin sees it: the option label for select-like fields. */
  displayValue: string;
}

/** "truncated": the field holds more than a snapshot can carry whole, so the model never saw its value. */
export type RejectionReason = "stale" | "unknown-path" | "excluded" | "read-only" | "truncated" | "invalid" | "unsupported";

export interface RejectedUpdate {
  update: FormUpdate;
  /** The field's label, when the field was found on the page. */
  label?: string;
  reason: RejectionReason;
  detail?: RejectionDetail;
  /** The kit validator that refused the value, when `detail` is "validator". */
  validator?: string;
}

export interface ApplyResult {
  applied: AppliedChange[];
  rejected: RejectedUpdate[];
}

export interface RevertResult {
  reverted: AppliedChange[];
  /** Changes whose form is no longer on the page. */
  missing: AppliedChange[];
  /** Changes whose form is on the page but whose field is not rendered right now. */
  hidden: AppliedChange[];
  /** Changes left alone because the admin has since given the field a value of their own. */
  superseded: AppliedChange[];
}
